import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import {
  reduxForm,
  Form,
  Field,
  Fields,
  change,
  reset,
  formValueSelector
} from 'redux-form';
import { Label } from 'reactstrap';

import * as validations from '../../Validation';
import Button from '../../components/Stuff/Button';
import renderField from '../../components/Fields/renderField';
import RenderSelect from '../../components/Fields/renderSelect';
import RenderAllErrors from '../../components/RenderAllErrors';
import { getCourses, getAwardType } from '../../modules/courses';
import { submitStart, submitSuccess, submitError } from '../../modules/apply';

import Http from '../../constants/Http';
import Loading from '../../components/Loading';
import Page from '../../components/Page';
import Meta from '../../components/Meta';

import '../ApplicationForm/index.scss';

const FORM_NAME = 'applicationStart';

const ApplicationStart = props => {
  const {
    handleSubmit,
    dispatch,
    courses,
    awardType,
    isSubmitting,
    hasError,
    courseValue,
    valid,
    submitFailed
  } = props;

  useEffect(() => {
    dispatch(getCourses()).then(data => {
      if (!data || !data.data) return;
      const urlParams = new URLSearchParams(window.location.search);
      const courseSlug = urlParams.get('course');
      if (!courseSlug) return;
      const course = data.data.find(c => c.slug === courseSlug);
      if (course) {
        dispatch(change(FORM_NAME, 'course', course));
      }
    });
  }, [dispatch]);

  const submit = values =>
    new Promise((resolve, reject) => {
      const payload = {
        course: values.course,
        award_type: values.award_type,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone
      };
      dispatch(submitStart());
      Http.post('/apply/start', payload)
        .then(() => {
          resolve();
          dispatch(submitSuccess());
          dispatch(push('/application-success'));
          dispatch(reset(FORM_NAME));
        })
        .catch(() => {
          reject();
          dispatch(submitError());
        });
    });

  if (isSubmitting) {
    return (
      <div style={{ minHeight: 500, display: 'flex', alignItems: 'center' }}>
        <Loading />
      </div>
    );
  }

  if (hasError) {
    return (
      <div
        style={{ minHeight: 500, display: 'flex', alignItems: 'center' }}
        className="apply-submission-status">
        <i className="fas fa-times-circle" />
        <div className="title">Something went wrong!</div>
        <p>There has been a problem submitting your application.</p>
        <p>
          Please call a member of our team on +44 (0)1273 704 463 for
          assistance.
        </p>
      </div>
    );
  }

  const fieldNames = [
    'course',
    'award_type',
    'firstName',
    'lastName',
    'email',
    'phone'
  ];

  return (
    <Page classnames="application-form-page">
      <Meta
        title="Start Your Application | Brighton School of Business & Management"
        description=""
        type="website"
      />
      <div className="apply-wrapper applyWrapper pt-5">
        <div className="wrapper mt-5 container d-flex justify-content-center">
          <div className="col-12 col-lg-8 mt-5">
            <div className="form-header">
              <div className="apply-tag">Start Your Application</div>
              <div className="course-title">
                {courseValue && courseValue.course_name
                  ? courseValue.course_name
                  : 'Choose a course'}
              </div>
              <div className="form-stage">
                <div className="row">
                  <div className="col-12">
                    <h5>Your Details</h5>
                  </div>
                </div>
              </div>
            </div>
            <Form onSubmit={handleSubmit(submit)}>
              <div className="form-group">
                <Label>Select the course *</Label>
                <Field
                  name="course"
                  component={RenderSelect}
                  clearable={false}
                  labelKey="course_name"
                  valueKey="id"
                  options={courses !== null ? courses.data : []}
                  validate={validations.required}
                />
              </div>

              {awardType !== null && awardType.length > 0 && (
                <div className="form-group">
                  <Label>Select the level *</Label>
                  <Field
                    name="award_type"
                    component={RenderSelect}
                    clearable={false}
                    labelKey="label"
                    valueKey="id"
                    options={awardType}
                    validate={validations.required}
                  />
                </div>
              )}

              <div className="form-group">
                <Field
                  name="firstName"
                  type="text"
                  autofocus
                  component={renderField}
                  label="First Name *"
                  validate={validations.required}
                />
              </div>
              <div className="form-group">
                <Field
                  name="lastName"
                  type="text"
                  component={renderField}
                  label="Last Name *"
                  validate={validations.required}
                />
              </div>
              <div className="form-group">
                <Field
                  name="email"
                  type="email"
                  component={renderField}
                  label="Email *"
                  validate={[validations.required, validations.email]}
                />
              </div>
              <div className="form-group">
                <Field
                  name="phone"
                  type="phone"
                  component={renderField}
                  label="Phone *"
                  validate={[validations.required, validations.phoneNumber]}
                />
              </div>
              <div className="form-group text-center">
                <Button
                  width={'100%'}
                  height={60}
                  style={{ width: '100%' }}
                  btnColor="rgba(77, 199, 117, 1)"
                  boxShadow="0 20px 24px 0 rgba(77,199,117,0.20)"
                  textColor="white"
                  fontSize={14}>
                  SUBMIT
                </Button>
              </div>
              {!valid && submitFailed && (
                <Fields names={fieldNames} component={RenderAllErrors} />
              )}
            </Form>
          </div>
        </div>
        <img
          src="/images/circles/topRightBlue.svg"
          height={250}
          className="topRight"
          style={{ position: 'absolute', top: 0, right: 0, zIndex: 500 }}
        />
        <img
          src="/images/circles/leftLightBlue.svg"
          height={300}
          className="topLeft"
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 500 }}
        />
      </div>
    </Page>
  );
};

const selector = formValueSelector(FORM_NAME);

const mapStateToProps = state => ({
  courses: state.courses.courses,
  awardType: state.courses.awardType !== null ? state.courses.awardType : null,
  isSubmitting: state.apply.submitting,
  hasError: state.apply.error,
  courseValue: selector(state, 'course')
});

export default connect(mapStateToProps)(
  reduxForm({
    form: FORM_NAME,
    destroyOnUnmount: true,
    forceUnregisterOnUnmount: true,
    onChange: (values, dispatch, _props, prevValues) => {
      const next = values.course;
      const prev = prevValues.course;
      if (next && (!prev || next.slug !== prev.slug)) {
        dispatch(getAwardType(next.slug));
      }
    }
  })(ApplicationStart)
);
